import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { administratorsApiService, CondominiumAdministrator, CreateAdministratorDTO, UpdateAdministratorDTO } from '../api/administrators-api';

// Estado inicial
interface AdministratorsState {
  administrators: CondominiumAdministrator[];
  currentAdministrator: CondominiumAdministrator | null;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters: {
    search?: string;
    status?: string;
    role?: string;
  };
}

const initialState: AdministratorsState = {
  administrators: [],
  currentAdministrator: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  filters: {},
};

// Async Thunks
export const fetchAdministrators = createAsyncThunk(
  'administrators/fetchAdministrators',
  async (filters: { page?: number; limit?: number; search?: string; status?: string; role?: string } = {}, { rejectWithValue }) => {
    try {
      const administrators = await administratorsApiService.getAdministrators();
      // Aplicar filtros localmente si es necesario
      let filtered = administrators;
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        filtered = filtered.filter(admin => 
          admin.email.toLowerCase().includes(searchLower) ||
          admin.firstName.toLowerCase().includes(searchLower) ||
          admin.lastName.toLowerCase().includes(searchLower) ||
          (admin.phone && admin.phone.toLowerCase().includes(searchLower))
        );
      }
      if (filters.status) {
        filtered = filtered.filter(admin => admin.status === filters.status);
      }
      if (filters.role) {
        filtered = filtered.filter(admin => admin.role === filters.role);
      }
      
      // Paginación local
      const page = filters.page || 1;
      const limit = filters.limit || 10;
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginated = filtered.slice(startIndex, endIndex);
      const totalPages = Math.ceil(filtered.length / limit);
      
      return {
        administrators: paginated,
        total: filtered.length,
        page,
        limit,
        totalPages,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar administradores'
      );
    }
  }
);

export const fetchAdministrator = createAsyncThunk(
  'administrators/fetchAdministrator',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await administratorsApiService.getAdministratorById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar administrador'
      );
    }
  }
);

export const createAdministrator = createAsyncThunk(
  'administrators/createAdministrator',
  async (data: CreateAdministratorDTO, { rejectWithValue }) => {
    try {
      const response = await administratorsApiService.createAdministrator(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al crear administrador'
      );
    }
  }
);

export const updateAdministrator = createAsyncThunk(
  'administrators/updateAdministrator',
  async (
    { id, data }: { id: string; data: UpdateAdministratorDTO },
    { rejectWithValue }
  ) => {
    try {
      const response = await administratorsApiService.updateAdministrator(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al actualizar administrador'
      );
    }
  }
);

export const deleteAdministrator = createAsyncThunk(
  'administrators/deleteAdministrator',
  async (id: string, { rejectWithValue }) => {
    try {
      await administratorsApiService.deleteAdministrator(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al eliminar administrador'
      );
    }
  }
);

export const toggleAdministratorStatus = createAsyncThunk(
  'administrators/toggleAdministratorStatus',
  async (id: string, { rejectWithValue }) => {
    try {
      const administrator = await administratorsApiService.getAdministratorById(id);
      const newStatus = administrator.status === 'active' ? 'inactive' : 'active';
      const response = await administratorsApiService.updateAdministrator(id, { status: newStatus });
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cambiar estado del administrador'
      );
    }
  }
);

// Slice
const administratorsSlice = createSlice({
  name: 'administrators',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<AdministratorsState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: state => {
      state.filters = {};
    },
    setCurrentAdministrator: (state, action: PayloadAction<CondominiumAdministrator | null>) => {
      state.currentAdministrator = action.payload;
    },
    clearError: state => {
      state.error = null;
    },
    setPagination: (state, action: PayloadAction<{ page: number; limit: number }>) => {
      state.pagination.page = action.payload.page;
      state.pagination.limit = action.payload.limit;
    },
  },
  extraReducers: builder => {
    // Fetch administrators
    builder
      .addCase(fetchAdministrators.pending, state => {
        if (state.administrators.length === 0) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchAdministrators.fulfilled, (state, action) => {
        state.loading = false;
        state.administrators = action.payload.administrators || [];
        state.pagination = {
          page: action.payload.page || 1,
          limit: action.payload.limit || 10,
          total: action.payload.total || 0,
          totalPages: action.payload.totalPages || 0,
        };
        })
      .addCase(fetchAdministrators.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        });

    // Fetch administrator
    builder
      .addCase(fetchAdministrator.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdministrator.fulfilled, (state, action) => {
        state.loading = false;
        state.currentAdministrator = action.payload;
      })
      .addCase(fetchAdministrator.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create administrator
    builder
      .addCase(createAdministrator.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAdministrator.fulfilled, (state, action) => {
        state.loading = false;
        state.administrators.unshift(action.payload);
        state.pagination.total += 1;
      })
      .addCase(createAdministrator.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update administrator
    builder
      .addCase(updateAdministrator.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAdministrator.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.administrators.findIndex(
          admin => admin.id === action.payload.id
        );
        if (index !== -1) {
          state.administrators[index] = action.payload;
        }
        if (state.currentAdministrator?.id === action.payload.id) {
          state.currentAdministrator = action.payload;
        }
      })
      .addCase(updateAdministrator.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Delete administrator
    builder
      .addCase(deleteAdministrator.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAdministrator.fulfilled, (state, action) => {
        state.loading = false;
        state.administrators = state.administrators.filter(
          admin => admin.id !== action.payload
        );
        state.pagination.total -= 1;
        if (state.currentAdministrator?.id === action.payload) {
          state.currentAdministrator = null;
        }
      })
      .addCase(deleteAdministrator.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Toggle administrator status
    builder
      .addCase(toggleAdministratorStatus.pending, state => {
        state.error = null;
      })
      .addCase(toggleAdministratorStatus.fulfilled, (state, action) => {
        const updatedAdministrator = action.payload;
        const index = state.administrators.findIndex(
          admin => admin.id === updatedAdministrator.id
        );
        if (index !== -1) {
          state.administrators[index] = updatedAdministrator;
        }
        if (state.currentAdministrator?.id === updatedAdministrator.id) {
          state.currentAdministrator = updatedAdministrator;
        }
      })
      .addCase(toggleAdministratorStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setFilters,
  clearFilters,
  setCurrentAdministrator,
  clearError,
  setPagination,
} = administratorsSlice.actions;

// Selectors
export const selectAdministrators = (state: { administrators: AdministratorsState }) => state.administrators.administrators;
export const selectCurrentAdministrator = (state: { administrators: AdministratorsState }) => state.administrators.currentAdministrator;
export const selectAdministratorsLoading = (state: { administrators: AdministratorsState }) => state.administrators.loading;
export const selectAdministratorsError = (state: { administrators: AdministratorsState }) => state.administrators.error;
export const selectAdministratorsPagination = (state: { administrators: AdministratorsState }) => state.administrators.pagination;
export const selectAdministratorsFilters = (state: { administrators: AdministratorsState }) => state.administrators.filters;

export default administratorsSlice.reducer;

